<template>
  <div class="container-fluid p-0 table-fix-head" id="content-page-table" :style="fixHeadCalculatedStyle">
    <table v-if="uiConsumptions.length > 0" class="table table-hover align-middle">
      <thead>
        <tr>
          <th>{{ $t('field.generic.id') }}</th>
          <th>{{ $t('field.masterdata.patientCode') }}</th>
          <th>{{ $t('field.consumption.billableShort') }}</th>
          <th>{{ $t('field.generic.status') }}</th>
          <th>{{ $t('field.export.exportStatus') }}</th>
          <th>{{ $t('field.export.exportReference') }}</th>
          <th>{{ $t('field.export.exportResult') }}</th>
          <th>{{ $t('field.generic.linesCount') }}</th>
          <th>
            {{ $t('field.generic.created') }}
            <font-awesome-icon :icon="['fas', 'sort-down']" />
          </th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="entry in uiConsumptions" :key="entry.id">
          <td @click="emit('selected', entry.id)">
            <button class="btn btn-link" type="button">
              {{ entry.id.toString().padStart(6, '0') }}
            </button>
          </td>
          <td>{{ entry.referenceCode }}</td>
          <td>
            <font-awesome-layers fixed-width class="fa-lg">
              <template v-if="entry.nonBillable">
                <font-awesome-icon :icon="['fas', 'slash']" :mask="['fas', 'coins']" class="text-muted" />
                <font-awesome-icon :icon="['fas', 'slash']" transform="up-1 right-1" class="text-danger" />
              </template>
              <template v-else>
                <font-awesome-icon :icon="['fas', 'coins']" class="text-success" />
              </template>
            </font-awesome-layers>
          </td>
          <td><ConsumptionStatus :status="entry.status"/></td>
          <td><ConsumptionExportStatus :status="entry.exportStatus"/></td>
          <td>{{ entry.exportReference }}</td>
          <td>{{ entry.exportResult }}</td>
          <td class="text-end"><span class="badge text-bg-secondary">{{ entry.linesCount }}</span></td>
          <AuditCreatedField :audit="entry.audit" />
          <td>
            <button v-if="entry.status === 0" class="btn btn-outline-danger" type="button" @click="removeConsumption(entry.id)">
              <font-awesome-icon :icon="['fas', 'trash']" />
            </button>
          </td>
        </tr>
      </tbody>
    </table>
    <span v-else>{{ $t('error.noData') }}</span>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useConsumptionsStore,useSitesStore } from '@/stores';
import { AuditCreatedField } from "@/components/fields";
import ConsumptionStatus from '@/components/consumptions/ConsumptionStatus.vue';
import ConsumptionExportStatus from '@/components/consumptions/ConsumptionExportStatus.vue';
import Consumption from '@/models/frontend/Consumption';

const sitesStore = useSitesStore();
const consumptionsStore = useConsumptionsStore();

const { data: consumptions } = storeToRefs(consumptionsStore);

const offsetTop = ref(0);

const emit = defineEmits<{
  (e: 'selected', id: number): void
}>();

onMounted(() => {
  offsetTop.value = document.querySelector('#content-page-table')?.getBoundingClientRect().top ?? 0;
});

const fixHeadCalculatedStyle = computed(() => {
  return {
    height: `calc(100vh - ${offsetTop.value}px)`,
  };
});

const uiConsumptions = computed(() => {
  const mappedConsumptions = consumptions.value.map((consumption) => new Consumption(
    consumption.id,
    sitesStore.getById(consumption.siteId),
    consumption.referenceCode,
    consumption.nonBillable,
    consumption.status,
    consumption.exportStatus,
    consumption.exportReference,
    consumption.exportResult,
    consumption.lines.length,
    consumption.audit));
  return [...mappedConsumptions].sort((a, b) => {
    return a.audit.createdAt > b.audit.createdAt ? -1 : 1;
  });
});

const removeConsumption = async (id: number) => {
  await consumptionsStore.delete(id);
};
</script>
